import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  InputNumber,
  Progress,
  Alert,
  Table,
  Space,
  Spin,
  Empty,
  Divider,
  Drawer,
  Segmented,
  Input,
  Descriptions,
  message,
} from 'antd';
import {
  CrownOutlined,
  CheckCircleFilled,
  ShoppingCartOutlined,
  FileTextOutlined,
  ReloadOutlined,
  TagOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import billingService, { loadRazorpayCheckout } from '../services/billingService';
import useAuthStore from '../store/authStore';

const { Title, Text, Paragraph } = Typography;

const formatINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatLimit = (n, suffix = '') =>
  n === -1 ? '∞ Unlimited' : `${Number(n || 0).toLocaleString('en-IN')}${suffix}`;

const STATUS_COLOR = {
  created: 'default',
  authorized: 'warning',
  captured: 'success',
  failed: 'error',
  refunded: 'magenta',
  partially_refunded: 'magenta',
  cancelled: 'default',
  expired: 'default',
};

const BillingSettings = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [transactions, setTransactions] = useState([]);
  // Tracks which plan cards have been expanded to show all features.
  const [expandedFeatures, setExpandedFeatures] = useState({});

  // Checkout drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPlan, setDrawerPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [userCount, setUserCount] = useState(5);
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [quote, setQuote] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, plansRes, usageRes, txnRes] = await Promise.all([
        billingService.getConfig(),
        billingService.getPlans(),
        billingService.getUsage(),
        billingService.listTransactions({ limit: 25 }),
      ]);
      setConfig(cfgRes.data?.data || cfgRes.data || null);
      setPlans(plansRes.data?.data || []);
      setUsage(usageRes.data?.data || null);
      setTransactions(txnRes.data?.data?.items || []);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to load billing info');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const isPerUser = drawerPlan?.pricingType === 'per_user';
  const annualSupported = (drawerPlan?.annualDiscountPercent || 0) > 0;

  // Re-quote whenever any input in the drawer changes.
  useEffect(() => {
    if (!drawerOpen || !drawerPlan) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await billingService.quote({
          planId: drawerPlan._id,
          userCount: isPerUser ? userCount : 1,
          billingCycle,
          couponCode: appliedCoupon?.code || null,
        });
        if (!cancelled) setQuote(res.data?.data || null);
      } catch (err) {
        if (!cancelled) setQuote(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [drawerOpen, drawerPlan, billingCycle, userCount, appliedCoupon, isPerUser]);

  const openCheckoutDrawer = (plan) => {
    setDrawerPlan(plan);
    setBillingCycle((plan.annualDiscountPercent || 0) > 0 ? 'monthly' : (plan.billingPeriod === 'yearly' ? 'yearly' : 'monthly'));
    setUserCount(5);
    setCouponInput('');
    setAppliedCoupon(null);
    setCouponError(null);
    setQuote(null);
    setDrawerOpen(true);
  };

  const closeCheckoutDrawer = () => {
    if (purchasing) return;
    setDrawerOpen(false);
  };

  const handleApplyCoupon = async () => {
    if (!couponInput.trim() || !drawerPlan) return;
    setCouponChecking(true);
    setCouponError(null);
    try {
      const res = await billingService.quote({
        planId: drawerPlan._id,
        userCount: isPerUser ? userCount : 1,
        billingCycle,
        couponCode: couponInput.trim(),
      });
      const data = res.data?.data;
      if (data?.coupon) {
        setAppliedCoupon(data.coupon);
        setCouponError(null);
        message.success(`Coupon ${data.coupon.code} applied — ${data.coupon.discountPercent}% off`);
      } else if (data?.couponError) {
        setAppliedCoupon(null);
        setCouponError(data.couponError.message || 'Invalid coupon');
      }
    } catch (err) {
      setAppliedCoupon(null);
      setCouponError(err.response?.data?.message || err.message);
    } finally {
      setCouponChecking(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError(null);
    setCouponInput('');
  };

  const handleBuy = async () => {
    if (!drawerPlan) return;
    if (!config?.configured) {
      message.error('Payments are not configured yet. Contact support.');
      return;
    }
    setPurchasing(true);
    try {
      const ready = await loadRazorpayCheckout();
      if (!ready) {
        message.error('Could not load Razorpay Checkout. Please retry.');
        setPurchasing(false);
        return;
      }
      const res = await billingService.createOrder({
        planId: drawerPlan._id,
        userCount: isPerUser ? userCount : 1,
        billingCycle,
        couponCode: appliedCoupon?.code || null,
      });
      const { order, keyId } = res.data?.data || {};
      if (!order?.id) throw new Error('Order not created');

      const rzp = new window.Razorpay({
        key: keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        name: 'QQCRM',
        description: `${drawerPlan.name}${isPerUser ? ` × ${userCount} users` : ''} (${billingCycle})`,
        prefill: {
          name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
          email: user?.email,
        },
        theme: { color: '#1e40af' },
        handler: async (response) => {
          try {
            await billingService.verify({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            message.success(`Payment successful — ${isPerUser ? `+${userCount} users` : 'plan activated'}`);
            setDrawerOpen(false);
            await refreshAll();
          } catch (err) {
            message.error(err.response?.data?.message || 'Payment verification failed');
          } finally {
            setPurchasing(false);
          }
        },
        modal: { ondismiss: () => setPurchasing(false) },
      });
      rzp.on('payment.failed', (resp) => {
        message.error(resp?.error?.description || 'Payment failed');
        setPurchasing(false);
      });
      rzp.open();
    } catch (err) {
      message.error(err.response?.data?.message || err.message || 'Could not start checkout');
      setPurchasing(false);
    }
  };

  const usagePercent = useMemo(() => {
    if (!usage || !usage.maxUsers) return 0;
    return Math.min(100, Math.round((usage.users / usage.maxUsers) * 100));
  }, [usage]);

  const txnColumns = [
    { title: 'Invoice #', dataIndex: 'invoiceNumber', render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Date', dataIndex: 'createdAt', render: (v) => new Date(v).toLocaleString('en-IN') },
    { title: 'Plan', dataIndex: 'planName' },
    { title: 'Cycle', dataIndex: 'billingPeriod', render: (v) => <Tag>{v}</Tag> },
    { title: 'Users', render: (_, r) => r.notes?.userCount || '—' },
    { title: 'Coupon', dataIndex: 'couponCode', render: (v) => (v ? <Tag color="orange">{v}</Tag> : <Text type="secondary">—</Text>) },
    { title: 'Amount', dataIndex: 'amount', align: 'right', render: (v) => formatINR((v || 0) / 100) },
    { title: 'Status', dataIndex: 'status', render: (s) => <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag> },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, r) =>
        r.status === 'captured' ? (
          <Button size="small" icon={<FileTextOutlined />} onClick={() => window.open(billingService.invoiceUrl(r._id), '_blank')}>
            Invoice
          </Button>
        ) : null,
    },
  ];

  if (loading && !plans.length) {
    return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;
  }

  const activePlan = usage?.activePlan;

  return (
    <div className="space-y-4">
      <div style={{ marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>Billing &amp; user limits</Title>
        <Text type="secondary">
          Manage your plan, buy more user seats, and track invoices. Tax invoices are generated automatically on payment.
        </Text>
      </div>

      {/* CURRENT PLAN + LIMITS */}
      <Card
        style={{
          borderRadius: 12,
          background: activePlan?.metadata?.color
            ? `linear-gradient(135deg, ${activePlan.metadata.color}15, #ffffff)`
            : 'linear-gradient(135deg, #eef2ff, #fdf2f8)',
        }}
      >
        <Row gutter={[24, 16]} align="top">
          <Col xs={24} md={10}>
            <Space size={4} style={{ marginBottom: 6 }}>
              <Tag color="purple" icon={<CrownOutlined />} style={{ fontSize: 12 }}>
                Current plan
              </Tag>
              {usage?.planExpiry && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Expires: {new Date(usage.planExpiry).toLocaleDateString('en-IN')}
                </Text>
              )}
            </Space>
            <Title level={3} style={{ margin: '4px 0' }}>
              {activePlan?.name || (usage?.plan ? usage.plan.toUpperCase() : 'Free')}
            </Title>
            {activePlan?.description && (
              <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }} ellipsis={{ rows: 2 }}>
                {activePlan.description}
              </Paragraph>
            )}
            <Space wrap>
              {activePlan?.price > 0 ? (
                <Tag color="blue">
                  {formatINR(activePlan.price)}
                  {activePlan.pricingType === 'per_user' ? ' / user' : ''} / {activePlan.billingPeriod}
                </Tag>
              ) : (
                <Tag color="green">Free</Tag>
              )}
              {activePlan?.annualDiscountPercent > 0 && (
                <Tag color="gold">{activePlan.annualDiscountPercent}% off annually</Tag>
              )}
            </Space>
          </Col>
          <Col xs={24} md={14}>
            <Text type="secondary" style={{ fontSize: 12 }}>Usage</Text>
            <Title level={5} style={{ margin: '2px 0 6px' }}>
              {usage?.users ?? 0} <Text type="secondary" style={{ fontSize: 13 }}>/ {usage?.maxUsers ?? 0} users</Text>
            </Title>
            <Progress
              percent={usagePercent}
              status={usagePercent >= 100 ? 'exception' : usagePercent >= 80 ? 'active' : 'normal'}
              strokeColor={usagePercent >= 100 ? '#ef4444' : usagePercent >= 80 ? '#f59e0b' : '#1e40af'}
            />
            <Descriptions size="small" column={2} style={{ marginTop: 12 }}>
              <Descriptions.Item label="Max users">{formatLimit(activePlan?.limits?.maxUsers ?? usage?.maxUsers)}</Descriptions.Item>
              <Descriptions.Item label="Max leads">{formatLimit(activePlan?.limits?.maxLeads)}</Descriptions.Item>
              <Descriptions.Item label="Max deals">{formatLimit(activePlan?.limits?.maxDeals)}</Descriptions.Item>
              <Descriptions.Item label="Storage">{formatLimit(activePlan?.limits?.maxStorage, ' GB')}</Descriptions.Item>
              <Descriptions.Item label="Integrations" span={2}>{formatLimit(activePlan?.limits?.maxIntegrations)}</Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {!config?.configured && (
        <Alert
          showIcon
          type="warning"
          message="Payments not configured"
          description="The Razorpay keys are missing on the server. Plan purchases are disabled until that's set up."
        />
      )}

      {/* AVAILABLE PLANS */}
      <Card
        title="Available plans"
        extra={
          <Button size="small" icon={<ReloadOutlined />} onClick={refreshAll}>
            Refresh
          </Button>
        }
      >
        {plans.length === 0 ? (
          <Empty description="No plans configured yet. Ask the super admin to publish plans." />
        ) : (
          <Row gutter={[16, 16]}>
            {plans.map((plan) => {
              const isCurrent = activePlan?._id === plan._id;
              const popular = plan.isPopular;
              return (
                <Col xs={24} md={12} lg={8} key={plan._id}>
                  <Card
                    hoverable
                    style={{
                      borderColor: isCurrent ? '#22c55e' : popular ? plan.metadata?.color || '#1e40af' : '#e5e7eb',
                      borderWidth: isCurrent || popular ? 2 : 1,
                      boxShadow: popular ? '0 6px 18px -8px rgba(37, 99, 235, 0.4)' : 'none',
                      height: '100%',
                    }}
                    bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Tag color={plan.metadata?.color ? undefined : 'blue'} style={{ background: plan.metadata?.color }}>
                        {plan.planType}
                      </Tag>
                      <Space size={4}>
                        {popular && <Tag color="gold">Popular</Tag>}
                        {isCurrent && <Tag color="success" icon={<CheckCircleFilled />}>Current</Tag>}
                      </Space>
                    </div>
                    <Title level={5} style={{ margin: 0 }}>{plan.name}</Title>
                    <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ fontSize: 12, margin: '6px 0 12px' }}>
                      {plan.description}
                    </Paragraph>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <Title level={3} style={{ margin: 0 }}>{formatINR(plan.price)}</Title>
                      <Text type="secondary">
                        {plan.pricingType === 'per_user' ? '/ user / ' : '/ '}{plan.billingPeriod}
                      </Text>
                    </div>
                    {plan.annualDiscountPercent > 0 && (
                      <Tag color="green" style={{ marginTop: 4, alignSelf: 'flex-start' }}>
                        Save {plan.annualDiscountPercent}% annually
                      </Tag>
                    )}
                    <div style={{ flex: 1, marginTop: 12 }}>
                      {(expandedFeatures[plan._id]
                        ? plan.features
                        : plan.features?.slice(0, 4) || []
                      ).map((f, i) => (
                        <div key={i} style={{ fontSize: 12, marginTop: 4, color: '#475569' }}>
                          ✓ {f.name}
                        </div>
                      ))}
                      {plan.features?.length > 4 && (
                        <Button
                          type="link"
                          size="small"
                          style={{
                            padding: 0,
                            height: 'auto',
                            fontSize: 11,
                            marginTop: 6,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedFeatures((prev) => ({
                              ...prev,
                              [plan._id]: !prev[plan._id],
                            }));
                          }}
                        >
                          {expandedFeatures[plan._id]
                            ? 'Show less'
                            : `+ ${plan.features.length - 4} more`}
                        </Button>
                      )}
                    </div>
                    <Button
                      type={isCurrent ? 'default' : 'primary'}
                      block
                      style={{
                        marginTop: 16,
                        ...(isCurrent
                          ? {}
                          : { background: 'linear-gradient(135deg, #1e40af, #3b82f6)', border: 'none' }),
                      }}
                      icon={<ShoppingCartOutlined />}
                      onClick={() => openCheckoutDrawer(plan)}
                      disabled={!config?.configured}
                    >
                      {isCurrent ? 'Manage / change' : `Choose ${plan.name}`}
                    </Button>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>

      {/* HISTORY */}
      <Card title="Purchase history">
        <Table
          rowKey="_id"
          size="small"
          dataSource={transactions}
          columns={txnColumns}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No purchases yet.' }}
        />
      </Card>

      {/* CHECKOUT DRAWER */}
      <Drawer
        title={drawerPlan ? `Subscribe: ${drawerPlan.name}` : 'Checkout'}
        open={drawerOpen}
        onClose={closeCheckoutDrawer}
        width={520}
        destroyOnClose
        footerStyle={{ padding: '16px 24px 96px' }}
        footer={
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <Button
              type="primary"
              size="large"
              icon={<ShoppingCartOutlined />}
              loading={purchasing}
              disabled={!config?.configured || !quote}
              onClick={handleBuy}
              style={{
                background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
                border: 'none',
                width: '100%',
                maxWidth: 360,
                height: 48,
                fontSize: 16,
                fontWeight: 600,
                boxShadow: '0 6px 16px -6px rgba(37, 99, 235, 0.6)',
              }}
            >
              Pay {quote ? formatINR(quote.amount?.totalRupees || 0) : ''}
            </Button>
            <Button type="text" onClick={closeCheckoutDrawer} disabled={purchasing}>
              Cancel
            </Button>
          </div>
        }
      >
        {drawerPlan && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Plan recap */}
            <Card size="small" bordered style={{ background: '#fafafa' }}>
              <Space size={8} style={{ marginBottom: 4 }}>
                <Tag color={drawerPlan.metadata?.color ? undefined : 'blue'} style={{ background: drawerPlan.metadata?.color }}>
                  {drawerPlan.planType}
                </Tag>
                {drawerPlan.isPopular && <Tag color="gold">Popular</Tag>}
              </Space>
              <Title level={5} style={{ margin: '4px 0' }}>{drawerPlan.name}</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatINR(drawerPlan.price)}
                {drawerPlan.pricingType === 'per_user' ? ' per user' : ''} per {drawerPlan.billingPeriod}
              </Text>
            </Card>

            {/* Cycle toggle — always shown so the user can pay monthly or
                yearly. The discount tag only appears when the plan offers
                an annual discount; otherwise yearly = 12× monthly. */}
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Billing cycle</Text>
              <div style={{ marginTop: 6 }}>
                <Segmented
                  block
                  value={billingCycle}
                  onChange={setBillingCycle}
                  options={[
                    { label: 'Monthly', value: 'monthly' },
                    {
                      label: (
                        <span>
                          Yearly
                          {annualSupported && (
                            <Tag color="green" style={{ marginInlineStart: 6 }}>
                              −{drawerPlan.annualDiscountPercent}%
                            </Tag>
                          )}
                        </span>
                      ),
                      value: 'yearly',
                    },
                  ]}
                />
              </div>
              {!annualSupported && (
                <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                  Pay upfront for 12 months. No annual discount on this plan.
                </Text>
              )}
            </div>

            {/* User count */}
            {isPerUser && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Users to add</Text>
                <InputNumber
                  min={1}
                  max={500}
                  value={userCount}
                  onChange={(v) => setUserCount(v || 1)}
                  style={{ width: '100%', marginTop: 6 }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Each unit increases your tenant's user limit by 1.
                </Text>
              </div>
            )}

            {/* Coupon */}
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <TagOutlined /> Coupon code
              </Text>
              {appliedCoupon ? (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginTop: 6 }}
                  message={
                    <Space>
                      <Text strong>{appliedCoupon.code}</Text>
                      <Tag color="green">−{appliedCoupon.discountPercent}%</Tag>
                    </Space>
                  }
                  action={<Button size="small" onClick={handleRemoveCoupon}>Remove</Button>}
                />
              ) : (
                <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                  <Input
                    placeholder="Enter code"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    onPressEnter={handleApplyCoupon}
                    status={couponError ? 'error' : ''}
                  />
                  <Button
                    icon={<ThunderboltOutlined />}
                    loading={couponChecking}
                    onClick={handleApplyCoupon}
                    disabled={!couponInput.trim()}
                  >
                    Apply
                  </Button>
                </Space.Compact>
              )}
              {couponError && !appliedCoupon && (
                <Text type="danger" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                  {couponError}
                </Text>
              )}
            </div>

            <Divider style={{ margin: '8px 0' }} />

            {/* Summary */}
            <div style={{ background: '#f8fafc', padding: 16, borderRadius: 10 }}>
              <Row justify="space-between" style={{ marginBottom: 6 }}>
                <Text type="secondary">
                  {isPerUser ? `${formatINR(drawerPlan.price)} × ${userCount} users` : 'Plan'}
                  {billingCycle === 'yearly' ? ' × 12 months' : ''}
                </Text>
                <Text>
                  {quote
                    ? formatINR(
                        quote.amount.basePaise / 100 +
                        (quote.amount.couponDiscountRupees || 0) +
                        (quote.amount.annualDiscountRupees || 0)
                      )
                    : '—'}
                </Text>
              </Row>
              {quote?.amount?.annualDiscountRupees > 0 && (
                <Row justify="space-between" style={{ marginBottom: 6 }}>
                  <Text type="secondary">Annual discount ({quote.amount.annualDiscountPercent}%)</Text>
                  <Text style={{ color: '#10b981' }}>−{formatINR(quote.amount.annualDiscountRupees)}</Text>
                </Row>
              )}
              {quote?.amount?.couponDiscountRupees > 0 && (
                <Row justify="space-between" style={{ marginBottom: 6 }}>
                  <Text type="secondary">Coupon ({quote.amount.couponCode})</Text>
                  <Text style={{ color: '#10b981' }}>−{formatINR(quote.amount.couponDiscountRupees)}</Text>
                </Row>
              )}
              <Row justify="space-between" style={{ marginBottom: 6 }}>
                <Text type="secondary">Subtotal</Text>
                <Text>{quote ? formatINR(quote.amount.baseRupees) : '—'}</Text>
              </Row>
              <Row justify="space-between" style={{ marginBottom: 6 }}>
                <Text type="secondary">GST (18%)</Text>
                <Text>{quote ? formatINR(quote.amount.taxRupees) : '—'}</Text>
              </Row>
              <Divider style={{ margin: '8px 0' }} />
              <Row justify="space-between" align="middle">
                <Text strong style={{ fontSize: 16 }}>Total payable</Text>
                <Text
                  strong
                  style={{
                    fontSize: 28,
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '-0.5px',
                  }}
                >
                  {quote ? formatINR(quote.amount.totalRupees) : '—'}
                </Text>
              </Row>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default BillingSettings;
